import * as React from 'react';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from './states';
import { cn } from '~/lib/utils';

/**
 * Tableau de donnees du back-office.
 *
 * Six ecrans repetaient la meme trentaine de lignes : `Card` > `Table` >
 * `TableHeader` > boucle de `TableRow`, plus les trois etats chargement, erreur et
 * vide. Chaque copie divergeait un peu — un alignement ici, un etat vide oublie la.
 *
 * Une colonne se declare par un objet : son intitule, comment lire la valeur, et
 * eventuellement son alignement. La declaration reste lisible d'un coup d'œil, ce
 * qu'une abstraction plus poussee (generation depuis un schema) ferait perdre.
 */

export interface Column<T> {
  /** Intitule affiche dans l'en-tete. */
  header: string;
  /** Contenu de la cellule. */
  cell: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
  /** Empeche le retour a la ligne — utile pour les dates et les references. */
  nowrap?: boolean;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  onRetry,
  emptyTitle = 'Aucune donnee',
  emptyDescription,
  emptyIcon,
  emptyAction,
}: {
  columns: Array<Column<T>>;
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  emptyAction?: React.ReactNode;
}) {
  // Les trois etats sont traites ici une fois pour toutes : aucun ecran ne peut
  // plus les oublier.
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return (
    <Card className="py-0">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.header}
                className={cn(column.align === 'right' && 'text-right')}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={rowKey(row)}>
              {columns.map((column) => (
                <TableCell
                  key={column.header}
                  className={cn(
                    column.align === 'right' && 'text-right',
                    column.nowrap && 'whitespace-nowrap',
                    column.className,
                  )}
                >
                  {column.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
